'use client'

import Script from 'next/script'
import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react'

const TERMLY_ORIGIN = 'https://app.termly.io'

// Intercom's launcher sits at z-index ~2147483000 and covered the banner's buttons.
// [data-termly-part] is Termly's supported styling hook; !important because Termly's own
// rule uses two generated classes and outranks a single attribute selector.
const BANNER_ABOVE_INTERCOM_CSS =
  '[data-termly-part="consent-banner"]{z-index:2147483647 !important}'

// Termly's undocumented root id: unlike the supported banner hook it also wraps the
// preferences modal, and as an id it outranks Termly's class rules without !important.
// Re-verify the id after Termly updates.
const HIDE_IN_PRINT_CSS =
  '@media print{#termly-code-snippet-support{display:none}}'

const CONSENT_CATEGORIES = [
  'essential',
  'performance',
  'analytics',
  'advertising',
  'social_networking',
  'unclassified',
] as const

export type TermlyConsentState = Record<
  (typeof CONSENT_CATEGORIES)[number],
  boolean
>

declare global {
  interface Window {
    Termly?: {
      getConsentState: () => TermlyConsentState | undefined
      on: (
        event: 'consent',
        handler: (data: { categories: string[] }) => void
      ) => void
    }
  }
}

interface TermlyContextValue {
  consentRequired: boolean
  /** Null until Termly has loaded and reported. */
  consent: TermlyConsentState | null
}

const NO_CONSENT_PLATFORM: TermlyContextValue = {
  consentRequired: false,
  consent: null,
}

const TermlyContext = createContext<TermlyContextValue>(NO_CONSENT_PLATFORM)

export function useTermlyConsent() {
  return useContext(TermlyContext)
}

function isWithdrawal(
  previous: TermlyConsentState,
  next: TermlyConsentState
): boolean {
  return CONSENT_CATEGORIES.some(
    (category) => previous[category] && !next[category]
  )
}

function consentFromCategories(categories: string[]): TermlyConsentState {
  return Object.fromEntries(
    CONSENT_CATEGORIES.map((category) => [
      category,
      categories.includes(category),
    ])
  ) as TermlyConsentState
}

export function TermlyProvider({
  websiteUuid,
  children,
}: {
  websiteUuid: string | null
  children: ReactNode
}) {
  const [consent, setConsent] = useState<TermlyConsentState | null>(null)
  const lastReported = useRef<TermlyConsentState | null>(null)

  if (!websiteUuid) {
    return (
      <TermlyContext.Provider value={NO_CONSENT_PLATFORM}>
        {children}
      </TermlyContext.Provider>
    )
  }

  // Auto-block off: each tracker is gated through useTermlyConsent instead, so Termly never
  // holds back Clerk, Slides or YouTube embeds it cannot classify.
  const src = new URL(`/resource-blocker/${websiteUuid}`, TERMLY_ORIGIN)
  src.searchParams.set('autoBlock', 'off')

  const handleLoad = () => {
    const termly = window.Termly
    if (!termly) {
      return
    }
    const applyConsent = (next: TermlyConsentState | null) => {
      const previous = lastReported.current
      lastReported.current = next
      // A tracker that already ran cannot be unloaded, so a withdrawn category needs a fresh page.
      if (previous && next && isWithdrawal(previous, next)) {
        window.location.reload()
        return
      }
      setConsent(next)
    }
    // Stored consent is readable synchronously once the script has run; the event covers later changes.
    applyConsent(termly.getConsentState() ?? null)
    termly.on('consent', ({ categories }) => {
      // A payload without the documented array is ignored rather than read as "all withdrawn".
      if (!Array.isArray(categories)) {
        return
      }
      applyConsent(consentFromCategories(categories))
    })
  }

  return (
    <TermlyContext.Provider value={{ consentRequired: true, consent }}>
      {children}
      <style href="termly-consent" precedence="default">
        {BANNER_ABOVE_INTERCOM_CSS + HIDE_IN_PRINT_CSS}
      </style>
      {/* Loaded after hydration on purpose: Termly prepends its banner to <body>, which breaks hydration if it runs first. */}
      <Script
        id="termly-cmp"
        src={src.toString()}
        strategy="afterInteractive"
        onLoad={handleLoad}
      />
    </TermlyContext.Provider>
  )
}
